import referenceNumberGuides from "@shared/referenceId/referenceNumberForCompany.json";

const DEFAULT_LINK_LABEL = "Where to find my account number";
const DEFAULT_LABEL = "Account number";
const DEFAULT_NO_VALUE_CHECKBOX_LABEL = "I don't know my account number";

export interface RawReferenceGuide {
  Client?: string;
  backendName?: string;
  Heading?: string;
  Content?: string;
  Image?: string;
  showImage?: boolean;
  isDefault?: boolean;
  label?: string;
  subtitle?: string;
  noValueCheckboxLabel?: string;
}

export interface ReferenceGuide {
  client: string;
  backendName?: string;
  heading: string;
  content: string;
  image?: string;
  showImage?: boolean;
  linkLabel: string;
  isDefault: boolean;
  label: string;
  subtitle: string;
  noValueCheckboxLabel: string;
}

export interface ReferenceGuideLookupParams {
  companyName?: string | null | undefined;
  backendName?: string | null | undefined;
}

const normalizeName = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  return value.trim().toLowerCase();
};

/**
 * Extract label from Heading field (e.g., "Barclays - Account Number" -> "Account Number")
 */
const extractLabelFromHeading = (heading: string): string => {
  const dashIndex = heading.lastIndexOf(" - ");
  if (dashIndex !== -1) {
    return heading.substring(dashIndex + 3).trim();
  }
  return DEFAULT_LABEL;
};

const sanitizedGuides: ReferenceGuide[] = [];

(referenceNumberGuides as RawReferenceGuide[]).forEach((entry) => {
  const client = entry.Client?.trim() || entry.backendName?.trim();
  const heading = entry.Heading?.trim();
  const content = entry.Content?.trim();

  if (!client || !heading || !content) {
    return;
  }

  // Extract label - prefer explicit label, fallback to parsing from Heading
  const label = entry.label?.trim() || extractLabelFromHeading(heading);

  // Extract subtitle - prefer explicit subtitle, fallback to heading
  const subtitle = entry.subtitle?.trim() || heading;

  // Generate noValueCheckboxLabel - prefer explicit, fallback to "I don't know my {label}"
  const noValueCheckboxLabel =
    entry.noValueCheckboxLabel?.trim() || `I don't know my ${label}`;

  const guide: ReferenceGuide = {
    client,
    heading,
    content,
    // Use subtitle (or heading) as the help link label so the inline helper shows the “where to find” text
    linkLabel: subtitle,
    isDefault: Boolean(entry.isDefault),
    label,
    subtitle,
    noValueCheckboxLabel,
  };

  const backendName = entry.backendName?.trim();
  if (backendName) {
    guide.backendName = backendName;
  }

  const image = entry.Image?.trim();
  if (image) {
    guide.image = image;
  }

  if (entry.showImage !== undefined) {
    guide.showImage = entry.showImage;
  }

  sanitizedGuides.push(guide);
});

const defaultGuide: ReferenceGuide =
  sanitizedGuides.find((guide) => guide.isDefault) ??
  sanitizedGuides[0] ?? {
    client: "Generic",
    heading: "How to find your account number",
    content:
      "1. Go to any letter or bill from your creditor.\n2. The account number is usually in the top-right corner.\n3. If you can't find it, check the account section on their website or mobile app.",
    linkLabel: DEFAULT_LINK_LABEL,
    isDefault: true,
    label: DEFAULT_LABEL,
    subtitle: "Where to find my account number",
    noValueCheckboxLabel: DEFAULT_NO_VALUE_CHECKBOX_LABEL,
  };

const guideLookup = new Map<string, ReferenceGuide>();

const registerGuideName = (name: string | undefined, guide: ReferenceGuide) => {
  const key = normalizeName(name);
  if (!key || guideLookup.has(key)) {
    return;
  }
  guideLookup.set(key, guide);
};

sanitizedGuides.forEach((guide) => {
  registerGuideName(guide.client, guide);
  registerGuideName(guide.backendName, guide);
});

export const REFERENCE_GUIDES = sanitizedGuides;

export function findReferenceGuideByName(
  name?: string | null | undefined
): ReferenceGuide | undefined {
  const key = normalizeName(name ?? undefined);
  if (!key) {
    return undefined;
  }
  return guideLookup.get(key);
}

export function getReferenceGuide({
  companyName,
  backendName,
}: ReferenceGuideLookupParams = {}): ReferenceGuide {
  const byCompany = guideLookup.get(normalizeName(companyName));
  if (byCompany) {
    return byCompany;
  }

  const byBackend = guideLookup.get(normalizeName(backendName));
  if (byBackend) {
    return byBackend;
  }

  return defaultGuide;
}

export function splitContentIntoSteps(content: string): string[] {
  return content
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}
